import { useState } from "react";
import { useKeyStore } from "../store/keyStore";
import { useAccelerandoStore } from "../store/accelerandoStore";

interface Props {
  onClose: () => void;
}

export function ApiKeyModal({ onClose }: Props) {
  const { babyai, anthropic, usePublic, setKey, setUsePublic } = useKeyStore();
  const accel = useAccelerandoStore();
  const [publicOn, setPublicOn] = useState(usePublic);
  const [babyaiKey, setBabyaiKey] = useState(babyai ?? "");
  const [anthropicKey, setAnthropicKey] = useState(anthropic ?? "");
  const [accelUrl, setAccelUrl] = useState(accel.baseUrl ?? "");
  const [accelTenant, setAccelTenant] = useState(accel.tenantId ?? "");
  const [accelKey, setAccelKey] = useState(accel.apiKey ?? "");

  function save() {
    setUsePublic(publicOn);
    if (babyaiKey.trim()) setKey("babyai", babyaiKey.trim());
    if (anthropicKey.trim()) setKey("anthropic", anthropicKey.trim());
    if (accelUrl.trim()) accel.setBaseUrl(accelUrl.trim().replace(/\/$/, ""));
    if (accelTenant.trim()) accel.setTenantId(accelTenant.trim());
    if (accelKey.trim()) accel.setApiKey(accelKey.trim());
    onClose();
  }

  // Save is always allowed — public mode counts as "configured."
  const canSave = true;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Keys</h2>
        <div className="subtitle">
          BYOK. Keys live in this browser only — never in the shared session file.
        </div>

        <div className="field">
          <label className="checkbox-row" style={{ marginBottom: 6 }}>
            <input
              type="checkbox"
              checked={publicOn}
              onChange={(e) => setPublicOn(e.target.checked)}
            />
            <span>
              Use public BabyAI <span style={{ color: "var(--fg-dimmer)" }}>(free shared instance)</span>
            </span>
          </label>
          <div style={{ fontSize: 11, color: "var(--fg-dim)", lineHeight: 1.5, paddingLeft: 22 }}>
            Free, no key needed. Your Mosh Pit picks become training data for
            BabyAI's routing — that's the whole point of giving it away. If you
            fill in your own HuggingFace token below, it takes precedence.
          </div>
        </div>

        <div className="field">
          <label>BabyAI token <span style={{ color: "var(--fg-dimmer)" }}>(optional — overrides public)</span></label>
          <input
            type="password"
            value={babyaiKey}
            onChange={(e) => setBabyaiKey(e.target.value)}
            placeholder="hf_…"
            autoComplete="off"
          />
        </div>

        <div className="field">
          <label>Anthropic key <span style={{ color: "var(--fg-dimmer)" }}>(fallback)</span></label>
          <input
            type="password"
            value={anthropicKey}
            onChange={(e) => setAnthropicKey(e.target.value)}
            placeholder="sk-ant-…"
            autoComplete="off"
          />
        </div>

        <div className="field">
          <label>Accelerando base URL <span style={{ color: "var(--fg-dimmer)" }}>(optional — enables ERP tools)</span></label>
          <input
            type="text"
            value={accelUrl}
            onChange={(e) => setAccelUrl(e.target.value)}
            placeholder="https://accelerando.workers.dev"
            autoComplete="off"
          />
        </div>

        <div className="field">
          <label>Accelerando tenant id <span style={{ color: "var(--fg-dimmer)" }}>(scopes every ERP tool call)</span></label>
          <input
            type="text"
            value={accelTenant}
            onChange={(e) => setAccelTenant(e.target.value)}
            placeholder="acme"
            autoComplete="off"
          />
        </div>

        <div className="field">
          <label>Accelerando API key <span style={{ color: "var(--fg-dimmer)" }}>(Bearer token the worker requires)</span></label>
          <input
            type="password"
            value={accelKey}
            onChange={(e) => setAccelKey(e.target.value)}
            placeholder="paste the key from `wrangler secret put`"
            autoComplete="off"
          />
        </div>

        <div className="actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={!canSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
